export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  graphql_public: {
    Tables: {
      [_ in never]: never
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      graphql: {
        Args: {
          extensions?: Json
          operationName?: string
          query?: string
          variables?: Json
        }
        Returns: Json
      }
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  public: {
    Tables: {
      accounts: {
        Row: {
          bank: string
          bic: string
          closed_at: string | null
          created_at: string
          currency: string
          external_id: string | null
          iban: string | null
          id: string
          name: string
          owner_entity: string
          settlement_ibans: string[]
          source_of_record: Database["public"]["Enums"]["account_source_of_record"]
          updated_at: string | null
        }
        Insert: {
          bank: string
          bic: string
          closed_at?: string | null
          created_at?: string
          currency: string
          external_id?: string | null
          iban?: string | null
          id?: string
          name: string
          owner_entity: string
          settlement_ibans?: string[]
          source_of_record: Database["public"]["Enums"]["account_source_of_record"]
          updated_at?: string | null
        }
        Update: {
          bank?: string
          bic?: string
          closed_at?: string | null
          created_at?: string
          currency?: string
          external_id?: string | null
          iban?: string | null
          id?: string
          name?: string
          owner_entity?: string
          settlement_ibans?: string[]
          source_of_record?: Database["public"]["Enums"]["account_source_of_record"]
          updated_at?: string | null
        }
        Relationships: []
      }
      audit_log: {
        Row: {
          action: Database["public"]["Enums"]["audit_action"]
          actor: string
          changed_fields: Json | null
          created_at: string
          entity_id: string
          entity_type: string
          id: string
          metadata: Json
          reason: string | null
        }
        Insert: {
          action: Database["public"]["Enums"]["audit_action"]
          actor: string
          changed_fields?: Json | null
          created_at?: string
          entity_id: string
          entity_type: string
          id?: string
          metadata?: Json
          reason?: string | null
        }
        Update: {
          action?: Database["public"]["Enums"]["audit_action"]
          actor?: string
          changed_fields?: Json | null
          created_at?: string
          entity_id?: string
          entity_type?: string
          id?: string
          metadata?: Json
          reason?: string | null
        }
        Relationships: []
      }
      categories: {
        Row: {
          created_at: string
          id: string
          name: string
          parent_id: string | null
          updated_at: string | null
          visible_to_employee: boolean
        }
        Insert: {
          created_at?: string
          id?: string
          name: string
          parent_id?: string | null
          updated_at?: string | null
          visible_to_employee?: boolean
        }
        Update: {
          created_at?: string
          id?: string
          name?: string
          parent_id?: string | null
          updated_at?: string | null
          visible_to_employee?: boolean
        }
        Relationships: [
          {
            foreignKeyName: "categories_parent_id_fkey"
            columns: ["parent_id"]
            isOneToOne: false
            referencedRelation: "categories"
            referencedColumns: ["id"]
          },
        ]
      }
      counterparties: {
        Row: {
          canonical_name: string
          created_at: string
          creditor_number: string | null
          default_category_id: string | null
          id: string
          metadata: Json
          type: Database["public"]["Enums"]["counterparty_type"]
          updated_at: string | null
        }
        Insert: {
          canonical_name: string
          created_at?: string
          creditor_number?: string | null
          default_category_id?: string | null
          id?: string
          metadata?: Json
          type: Database["public"]["Enums"]["counterparty_type"]
          updated_at?: string | null
        }
        Update: {
          canonical_name?: string
          created_at?: string
          creditor_number?: string | null
          default_category_id?: string | null
          id?: string
          metadata?: Json
          type?: Database["public"]["Enums"]["counterparty_type"]
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "counterparties_default_category_id_fkey"
            columns: ["default_category_id"]
            isOneToOne: false
            referencedRelation: "categories"
            referencedColumns: ["id"]
          },
        ]
      }
      counterparty_aliases: {
        Row: {
          active: boolean
          counterparty_id: string
          created_at: string
          id: string
          match_kind: Database["public"]["Enums"]["text_match_kind"]
          pattern: string
          priority: number
          updated_at: string | null
        }
        Insert: {
          active?: boolean
          counterparty_id: string
          created_at?: string
          id?: string
          match_kind: Database["public"]["Enums"]["text_match_kind"]
          pattern: string
          priority?: number
          updated_at?: string | null
        }
        Update: {
          active?: boolean
          counterparty_id?: string
          created_at?: string
          id?: string
          match_kind?: Database["public"]["Enums"]["text_match_kind"]
          pattern?: string
          priority?: number
          updated_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "counterparty_aliases_counterparty_id_fkey"
            columns: ["counterparty_id"]
            isOneToOne: false
            referencedRelation: "counterparties"
            referencedColumns: ["id"]
          },
        ]
      }
      document_assignment_tombstones: {
        Row: {
          created_at: string
          created_by: string
          document_id: string
          document_source: string | null
          document_source_id: string | null
          id: string
          reason: string
          transaction_id: string
        }
        Insert: {
          created_at?: string
          created_by: string
          document_id: string
          document_source?: string | null
          document_source_id?: string | null
          id?: string
          reason: string
          transaction_id: string
        }
        Update: {
          created_at?: string
          created_by?: string
          document_id?: string
          document_source?: string | null
          document_source_id?: string | null
          id?: string
          reason?: string
          transaction_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "document_assignment_tombstones_transaction_id_fkey"
            columns: ["transaction_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
        ]
      }
      document_assignments: {
        Row: {
          confidence: Database["public"]["Enums"]["confidence_level"]
          created_at: string
          created_by: string
          document_id: string
          id: string
          is_eigenbeleg: boolean
          reasoning: string | null
          source: Database["public"]["Enums"]["document_assignment_source"]
          transaction_id: string
        }
        Insert: {
          confidence: Database["public"]["Enums"]["confidence_level"]
          created_at?: string
          created_by: string
          document_id: string
          id?: string
          is_eigenbeleg?: boolean
          reasoning?: string | null
          source: Database["public"]["Enums"]["document_assignment_source"]
          transaction_id: string
        }
        Update: {
          confidence?: Database["public"]["Enums"]["confidence_level"]
          created_at?: string
          created_by?: string
          document_id?: string
          id?: string
          is_eigenbeleg?: boolean
          reasoning?: string | null
          source?: Database["public"]["Enums"]["document_assignment_source"]
          transaction_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "document_assignments_document_id_fkey"
            columns: ["document_id"]
            isOneToOne: false
            referencedRelation: "documents"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "document_assignments_transaction_id_fkey"
            columns: ["transaction_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
        ]
      }
      documents: {
        Row: {
          created_at: string
          extracted: Json | null
          extraction_status: Database["public"]["Enums"]["extraction_status"]
          file_hash: string
          id: string
          mime_type: string
          original_filename: string
          provenance: string | null
          source: Database["public"]["Enums"]["document_source"]
          source_id: string | null
          storage_path: string
          updated_at: string | null
        }
        Insert: {
          created_at?: string
          extracted?: Json | null
          extraction_status?: Database["public"]["Enums"]["extraction_status"]
          file_hash: string
          id?: string
          mime_type: string
          original_filename: string
          provenance?: string | null
          source: Database["public"]["Enums"]["document_source"]
          source_id?: string | null
          storage_path: string
          updated_at?: string | null
        }
        Update: {
          created_at?: string
          extracted?: Json | null
          extraction_status?: Database["public"]["Enums"]["extraction_status"]
          file_hash?: string
          id?: string
          mime_type?: string
          original_filename?: string
          provenance?: string | null
          source?: Database["public"]["Enums"]["document_source"]
          source_id?: string | null
          storage_path?: string
          updated_at?: string | null
        }
        Relationships: []
      }
      eigenbeleg_counters: {
        Row: {
          last_number: number
          owner_entity: string
          year: number
        }
        Insert: {
          last_number: number
          owner_entity: string
          year: number
        }
        Update: {
          last_number?: number
          owner_entity?: string
          year?: number
        }
        Relationships: []
      }
      export_batch_documents: {
        Row: {
          batch_id: string
          created_at: string
          document_id: string
          entry_filename: string | null
          id: string
        }
        Insert: {
          batch_id: string
          created_at?: string
          document_id: string
          entry_filename?: string | null
          id?: string
        }
        Update: {
          batch_id?: string
          created_at?: string
          document_id?: string
          entry_filename?: string | null
          id?: string
        }
        Relationships: [
          {
            foreignKeyName: "export_batch_documents_batch_id_fkey"
            columns: ["batch_id"]
            isOneToOne: false
            referencedRelation: "export_batches"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "export_batch_documents_document_id_fkey"
            columns: ["document_id"]
            isOneToOne: false
            referencedRelation: "documents"
            referencedColumns: ["id"]
          },
        ]
      }
      export_batches: {
        Row: {
          belegbilder_sha256: string | null
          belegbilder_size_bytes: number | null
          belegbilder_storage_path: string | null
          document_count: number
          excluded_count: number
          excluded_reasons: Json
          filter_spec: Json
          generated_at: string
          generated_by: string | null
          generated_by_email: string | null
          generated_by_role: string
          id: string
          period_label: string
          phase: number
          request_id: string | null
          tenant_settings_snapshot: Json
          zip_sha256: string
          zip_size_bytes: number
          zip_storage_path: string
        }
        Insert: {
          belegbilder_sha256?: string | null
          belegbilder_size_bytes?: number | null
          belegbilder_storage_path?: string | null
          document_count: number
          excluded_count?: number
          excluded_reasons?: Json
          filter_spec?: Json
          generated_at?: string
          generated_by?: string | null
          generated_by_email?: string | null
          generated_by_role?: string
          id?: string
          period_label: string
          phase: number
          request_id?: string | null
          tenant_settings_snapshot: Json
          zip_sha256: string
          zip_size_bytes: number
          zip_storage_path: string
        }
        Update: {
          belegbilder_sha256?: string | null
          belegbilder_size_bytes?: number | null
          belegbilder_storage_path?: string | null
          document_count?: number
          excluded_count?: number
          excluded_reasons?: Json
          filter_spec?: Json
          generated_at?: string
          generated_by?: string | null
          generated_by_email?: string | null
          generated_by_role?: string
          id?: string
          period_label?: string
          phase?: number
          request_id?: string | null
          tenant_settings_snapshot?: Json
          zip_sha256?: string
          zip_size_bytes?: number
          zip_storage_path?: string
        }
        Relationships: []
      }
      import_files: {
        Row: {
          account_id: string
          attempt_count: number
          byte_size: number
          commit_summary: Json | null
          committed_at: string | null
          created_at: string
          created_by: string
          file_hash: string
          id: string
          last_actor: string
          last_attempt_at: string
          last_error: Json | null
          original_filename: string
          row_count: number | null
          source: string
          status: Database["public"]["Enums"]["import_file_status"]
          storage_path: string
        }
        Insert: {
          account_id: string
          attempt_count?: number
          byte_size: number
          commit_summary?: Json | null
          committed_at?: string | null
          created_at?: string
          created_by: string
          file_hash: string
          id?: string
          last_actor: string
          last_attempt_at?: string
          last_error?: Json | null
          original_filename: string
          row_count?: number | null
          source?: string
          status: Database["public"]["Enums"]["import_file_status"]
          storage_path: string
        }
        Update: {
          account_id?: string
          attempt_count?: number
          byte_size?: number
          commit_summary?: Json | null
          committed_at?: string | null
          created_at?: string
          created_by?: string
          file_hash?: string
          id?: string
          last_actor?: string
          last_attempt_at?: string
          last_error?: Json | null
          original_filename?: string
          row_count?: number | null
          source?: string
          status?: Database["public"]["Enums"]["import_file_status"]
          storage_path?: string
        }
        Relationships: [
          {
            foreignKeyName: "import_files_account_id_fkey"
            columns: ["account_id"]
            isOneToOne: false
            referencedRelation: "accounts"
            referencedColumns: ["id"]
          },
        ]
      }
      match_attempts: {
        Row: {
          attempted_at: string
          created_by: string
          id: string
          outcome: Database["public"]["Enums"]["match_attempt_outcome"]
          reasoning: string | null
          source: Database["public"]["Enums"]["match_attempt_source"]
          transaction_id: string
        }
        Insert: {
          attempted_at?: string
          created_by: string
          id?: string
          outcome: Database["public"]["Enums"]["match_attempt_outcome"]
          reasoning?: string | null
          source: Database["public"]["Enums"]["match_attempt_source"]
          transaction_id: string
        }
        Update: {
          attempted_at?: string
          created_by?: string
          id?: string
          outcome?: Database["public"]["Enums"]["match_attempt_outcome"]
          reasoning?: string | null
          source?: Database["public"]["Enums"]["match_attempt_source"]
          transaction_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "match_attempts_transaction_id_fkey"
            columns: ["transaction_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
        ]
      }
      orchestrator_runs: {
        Row: {
          command: string
          cost_jsonb: Json
          created_at: string
          created_by: string
          ended_at: string | null
          id: string
          report_markdown: string | null
          scope_jsonb: Json
          started_at: string
          summary_jsonb: Json
        }
        Insert: {
          command: string
          cost_jsonb?: Json
          created_at?: string
          created_by?: string
          ended_at?: string | null
          id?: string
          report_markdown?: string | null
          scope_jsonb?: Json
          started_at: string
          summary_jsonb?: Json
        }
        Update: {
          command?: string
          cost_jsonb?: Json
          created_at?: string
          created_by?: string
          ended_at?: string | null
          id?: string
          report_markdown?: string | null
          scope_jsonb?: Json
          started_at?: string
          summary_jsonb?: Json
        }
        Relationships: []
      }
      receipt_chase_digests: {
        Row: {
          composed_at: string
          delivered_at: string | null
          id: string
          item_count: number
          message: string
          sent_on: string
          slack_ts: string | null
          slack_user_id: string
          team_member_id: string
          total_due: number
        }
        Insert: {
          composed_at?: string
          delivered_at?: string | null
          id?: string
          item_count: number
          message: string
          sent_on: string
          slack_ts?: string | null
          slack_user_id: string
          team_member_id: string
          total_due: number
        }
        Update: {
          composed_at?: string
          delivered_at?: string | null
          id?: string
          item_count?: number
          message?: string
          sent_on?: string
          slack_ts?: string | null
          slack_user_id?: string
          team_member_id?: string
          total_due?: number
        }
        Relationships: [
          {
            foreignKeyName: "receipt_chase_digests_team_member_id_fkey"
            columns: ["team_member_id"]
            isOneToOne: false
            referencedRelation: "team_members"
            referencedColumns: ["id"]
          },
        ]
      }
      receipt_chase_log: {
        Row: {
          cardholder_name: string
          channel: string
          digest_id: string | null
          id: string
          resolved_at: string | null
          resolved_via: string | null
          sent_at: string
          stage: number
          token_expires_at: string
          transaction_id: string
          upload_token: string
        }
        Insert: {
          cardholder_name: string
          channel: string
          digest_id?: string | null
          id?: string
          resolved_at?: string | null
          resolved_via?: string | null
          sent_at?: string
          stage: number
          token_expires_at: string
          transaction_id: string
          upload_token: string
        }
        Update: {
          cardholder_name?: string
          channel?: string
          digest_id?: string | null
          id?: string
          resolved_at?: string | null
          resolved_via?: string | null
          sent_at?: string
          stage?: number
          token_expires_at?: string
          transaction_id?: string
          upload_token?: string
        }
        Relationships: [
          {
            foreignKeyName: "receipt_chase_log_digest_id_fkey"
            columns: ["digest_id"]
            isOneToOne: false
            referencedRelation: "receipt_chase_digests"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "receipt_chase_log_transaction_id_fkey"
            columns: ["transaction_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
        ]
      }
      revolut_alerts: {
        Row: {
          details: string | null
          id: string
          kind: string
          observed_at: string
          revolut_sync_state_id: string | null
        }
        Insert: {
          details?: string | null
          id?: string
          kind: string
          observed_at?: string
          revolut_sync_state_id?: string | null
        }
        Update: {
          details?: string | null
          id?: string
          kind?: string
          observed_at?: string
          revolut_sync_state_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "revolut_alerts_revolut_sync_state_id_fkey"
            columns: ["revolut_sync_state_id"]
            isOneToOne: false
            referencedRelation: "revolut_sync_state"
            referencedColumns: ["id"]
          },
        ]
      }
      revolut_receipt_attempts: {
        Row: {
          attempts: number
          expense_id: string
          id: string
          last_attempt_at: string
          last_error: string
          next_attempt_at: string
          receipt_id: string
        }
        Insert: {
          attempts: number
          expense_id: string
          id?: string
          last_attempt_at?: string
          last_error: string
          next_attempt_at: string
          receipt_id: string
        }
        Update: {
          attempts?: number
          expense_id?: string
          id?: string
          last_attempt_at?: string
          last_error?: string
          next_attempt_at?: string
          receipt_id?: string
        }
        Relationships: []
      }
      revolut_sync_state: {
        Row: {
          assignments_inserted: number
          assignments_skipped_tombstoned: number
          chase_position: string | null
          documents_inserted: number
          documents_skipped_404: number
          documents_skipped_429: number
          documents_skipped_magic: number
          documents_skipped_mime: number
          expense_walk_stop_reason: string | null
          finished_at: string | null
          id: string
          last_error_message: string | null
          links_inserted: number
          max_expense_date_synced: string | null
          refresh_token_rotated: boolean
          started_at: string
          status: Database["public"]["Enums"]["revolut_sync_status"]
          trigger_kind: string
          txns_inserted: number
          txns_seen: number
        }
        Insert: {
          assignments_inserted?: number
          assignments_skipped_tombstoned?: number
          chase_position?: string | null
          documents_inserted?: number
          documents_skipped_404?: number
          documents_skipped_429?: number
          documents_skipped_magic?: number
          documents_skipped_mime?: number
          expense_walk_stop_reason?: string | null
          finished_at?: string | null
          id?: string
          last_error_message?: string | null
          links_inserted?: number
          max_expense_date_synced?: string | null
          refresh_token_rotated?: boolean
          started_at?: string
          status: Database["public"]["Enums"]["revolut_sync_status"]
          trigger_kind: string
          txns_inserted?: number
          txns_seen?: number
        }
        Update: {
          assignments_inserted?: number
          assignments_skipped_tombstoned?: number
          chase_position?: string | null
          documents_inserted?: number
          documents_skipped_404?: number
          documents_skipped_429?: number
          documents_skipped_magic?: number
          documents_skipped_mime?: number
          expense_walk_stop_reason?: string | null
          finished_at?: string | null
          id?: string
          last_error_message?: string | null
          links_inserted?: number
          max_expense_date_synced?: string | null
          refresh_token_rotated?: boolean
          started_at?: string
          status?: Database["public"]["Enums"]["revolut_sync_status"]
          trigger_kind?: string
          txns_inserted?: number
          txns_seen?: number
        }
        Relationships: []
      }
      rules: {
        Row: {
          confidence: Database["public"]["Enums"]["confidence_level"] | null
          created_at: string
          created_by: string
          effect_target_id: string
          id: string
          match_field: Database["public"]["Enums"]["rule_match_field"]
          match_kind: Database["public"]["Enums"]["text_match_kind"]
          match_value: string
          priority: number
          reasoning: string | null
          source: Database["public"]["Enums"]["rule_source"]
          status: Database["public"]["Enums"]["rule_status"]
          type: Database["public"]["Enums"]["rule_type"]
          updated_at: string | null
        }
        Insert: {
          confidence?: Database["public"]["Enums"]["confidence_level"] | null
          created_at?: string
          created_by: string
          effect_target_id: string
          id?: string
          match_field: Database["public"]["Enums"]["rule_match_field"]
          match_kind: Database["public"]["Enums"]["text_match_kind"]
          match_value: string
          priority?: number
          reasoning?: string | null
          source: Database["public"]["Enums"]["rule_source"]
          status?: Database["public"]["Enums"]["rule_status"]
          type: Database["public"]["Enums"]["rule_type"]
          updated_at?: string | null
        }
        Update: {
          confidence?: Database["public"]["Enums"]["confidence_level"] | null
          created_at?: string
          created_by?: string
          effect_target_id?: string
          id?: string
          match_field?: Database["public"]["Enums"]["rule_match_field"]
          match_kind?: Database["public"]["Enums"]["text_match_kind"]
          match_value?: string
          priority?: number
          reasoning?: string | null
          source?: Database["public"]["Enums"]["rule_source"]
          status?: Database["public"]["Enums"]["rule_status"]
          type?: Database["public"]["Enums"]["rule_type"]
          updated_at?: string | null
        }
        Relationships: []
      }
      team_member_cards: {
        Row: {
          active: boolean
          card_number_masked: string | null
          created_at: string
          id: string
          label: string | null
          revolut_card_id: string
          team_member_id: string
        }
        Insert: {
          active?: boolean
          card_number_masked?: string | null
          created_at?: string
          id?: string
          label?: string | null
          revolut_card_id: string
          team_member_id: string
        }
        Update: {
          active?: boolean
          card_number_masked?: string | null
          created_at?: string
          id?: string
          label?: string | null
          revolut_card_id?: string
          team_member_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "team_member_cards_team_member_id_fkey"
            columns: ["team_member_id"]
            isOneToOne: false
            referencedRelation: "team_members"
            referencedColumns: ["id"]
          },
        ]
      }
      team_members: {
        Row: {
          active: boolean
          cardholder_name: string
          chase_cadence_days: number[]
          created_at: string
          display_name: string
          escalation_days: number
          id: string
          slack_user_id: string
        }
        Insert: {
          active?: boolean
          cardholder_name: string
          chase_cadence_days?: number[]
          created_at?: string
          display_name: string
          escalation_days?: number
          id?: string
          slack_user_id: string
        }
        Update: {
          active?: boolean
          cardholder_name?: string
          chase_cadence_days?: number[]
          created_at?: string
          display_name?: string
          escalation_days?: number
          id?: string
          slack_user_id?: string
        }
        Relationships: []
      }
      tenant_settings: {
        Row: {
          id: string
          key: string
          updated_at: string
          updated_by: string | null
          value: string
        }
        Insert: {
          id?: string
          key: string
          updated_at?: string
          updated_by?: string | null
          value: string
        }
        Update: {
          id?: string
          key?: string
          updated_at?: string
          updated_by?: string | null
          value?: string
        }
        Relationships: []
      }
      transaction_dedup_decisions: {
        Row: {
          account_id: string
          decided_at: string
          decided_by: string
          decision: Database["public"]["Enums"]["dedup_decision"]
          evidence: Json
          id: string
          incoming_raw: Json
          incoming_source_id: string
          matched_transaction_ids: string[]
          seq: number
        }
        Insert: {
          account_id: string
          decided_at?: string
          decided_by: string
          decision: Database["public"]["Enums"]["dedup_decision"]
          evidence: Json
          id?: string
          incoming_raw: Json
          incoming_source_id: string
          matched_transaction_ids: string[]
          seq?: never
        }
        Update: {
          account_id?: string
          decided_at?: string
          decided_by?: string
          decision?: Database["public"]["Enums"]["dedup_decision"]
          evidence?: Json
          id?: string
          incoming_raw?: Json
          incoming_source_id?: string
          matched_transaction_ids?: string[]
          seq?: never
        }
        Relationships: [
          {
            foreignKeyName: "transaction_dedup_decisions_account_id_fkey"
            columns: ["account_id"]
            isOneToOne: false
            referencedRelation: "accounts"
            referencedColumns: ["id"]
          },
        ]
      }
      transaction_links: {
        Row: {
          confidence: Database["public"]["Enums"]["confidence_level"]
          created_at: string
          created_by: string
          detected_by: Database["public"]["Enums"]["transaction_link_detected_by"]
          id: string
          reasoning: string | null
          rule_id: string | null
          transaction_a_id: string
          transaction_b_id: string | null
          type: Database["public"]["Enums"]["transaction_link_type"]
        }
        Insert: {
          confidence: Database["public"]["Enums"]["confidence_level"]
          created_at?: string
          created_by: string
          detected_by: Database["public"]["Enums"]["transaction_link_detected_by"]
          id?: string
          reasoning?: string | null
          rule_id?: string | null
          transaction_a_id: string
          transaction_b_id?: string | null
          type: Database["public"]["Enums"]["transaction_link_type"]
        }
        Update: {
          confidence?: Database["public"]["Enums"]["confidence_level"]
          created_at?: string
          created_by?: string
          detected_by?: Database["public"]["Enums"]["transaction_link_detected_by"]
          id?: string
          reasoning?: string | null
          rule_id?: string | null
          transaction_a_id?: string
          transaction_b_id?: string | null
          type?: Database["public"]["Enums"]["transaction_link_type"]
        }
        Relationships: [
          {
            foreignKeyName: "transaction_links_rule_id_fkey"
            columns: ["rule_id"]
            isOneToOne: false
            referencedRelation: "rules"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "transaction_links_transaction_a_id_fkey"
            columns: ["transaction_a_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "transaction_links_transaction_b_id_fkey"
            columns: ["transaction_b_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
        ]
      }
      transaction_notes: {
        Row: {
          created_at: string
          created_by: string
          id: string
          note: string
          resolved_at: string | null
          transaction_id: string
        }
        Insert: {
          created_at?: string
          created_by: string
          id?: string
          note: string
          resolved_at?: string | null
          transaction_id: string
        }
        Update: {
          created_at?: string
          created_by?: string
          id?: string
          note?: string
          resolved_at?: string | null
          transaction_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "transaction_notes_transaction_id_fkey"
            columns: ["transaction_id"]
            isOneToOne: false
            referencedRelation: "transactions"
            referencedColumns: ["id"]
          },
        ]
      }
      transactions: {
        Row: {
          account_id: string
          amount_eur: number | null
          amount_original: number
          bank_provided_state: string | null
          booked_at: string
          cardholder_name: string | null
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string | null
          counterparty_id: string | null
          counterparty_raw_name: string
          created_at: string
          currency: string
          fx_rate: number | null
          fx_source: string | null
          id: string
          import_file_id: string | null
          mcc: string | null
          purpose: string
          raw: Json
          source_id: string
          value_date: string
        }
        Insert: {
          account_id: string
          amount_eur?: number | null
          amount_original: number
          bank_provided_state?: string | null
          booked_at: string
          cardholder_name?: string | null
          categorization_status?: Database["public"]["Enums"]["categorization_status"]
          category_id?: string | null
          counterparty_id?: string | null
          counterparty_raw_name: string
          created_at?: string
          currency: string
          fx_rate?: number | null
          fx_source?: string | null
          id?: string
          import_file_id?: string | null
          mcc?: string | null
          purpose: string
          raw: Json
          source_id: string
          value_date: string
        }
        Update: {
          account_id?: string
          amount_eur?: number | null
          amount_original?: number
          bank_provided_state?: string | null
          booked_at?: string
          cardholder_name?: string | null
          categorization_status?: Database["public"]["Enums"]["categorization_status"]
          category_id?: string | null
          counterparty_id?: string | null
          counterparty_raw_name?: string
          created_at?: string
          currency?: string
          fx_rate?: number | null
          fx_source?: string | null
          id?: string
          import_file_id?: string | null
          mcc?: string | null
          purpose?: string
          raw?: Json
          source_id?: string
          value_date?: string
        }
        Relationships: [
          {
            foreignKeyName: "transactions_account_id_fkey"
            columns: ["account_id"]
            isOneToOne: false
            referencedRelation: "accounts"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "transactions_category_id_fkey"
            columns: ["category_id"]
            isOneToOne: false
            referencedRelation: "categories"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "transactions_counterparty_id_fkey"
            columns: ["counterparty_id"]
            isOneToOne: false
            referencedRelation: "counterparties"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "transactions_import_file_id_fkey"
            columns: ["import_file_id"]
            isOneToOne: false
            referencedRelation: "import_files"
            referencedColumns: ["id"]
          },
        ]
      }
      user_roles: {
        Row: {
          created_at: string
          role: string
          user_id: string
        }
        Insert: {
          created_at?: string
          role: string
          user_id: string
        }
        Update: {
          created_at?: string
          role?: string
          user_id?: string
        }
        Relationships: []
      }
      vendor_portal_credentials: {
        Row: {
          last_used_at: string | null
          password: string
          updated_at: string
          username: string
          vendor_slug: string
        }
        Insert: {
          last_used_at?: string | null
          password: string
          updated_at?: string
          username: string
          vendor_slug: string
        }
        Update: {
          last_used_at?: string | null
          password?: string
          updated_at?: string
          username?: string
          vendor_slug?: string
        }
        Relationships: []
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      _account_period_txn_sum: {
        Args: {
          p_account_id: string
          p_from_date: string
          p_period: string
          p_to_date: string
        }
        Returns: number
      }
      _backfill_export_batch_generator_stamp: { Args: never; Returns: number }
      _receipt_digest_line: {
        Args: {
          p_amount: number
          p_booked_at: string
          p_cadence_len: number
          p_counterparty: string
          p_currency: string
          p_stage: number
        }
        Returns: string
      }
      _receipt_digest_message: {
        Args: { p_display_name: string; p_lines: string[]; p_remaining: number }
        Returns: string
      }
      _resolve_one: {
        Args: { in_mcc: string; in_purpose: string; in_raw_name: string }
        Returns: {
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string
          counterparty_id: string
        }[]
      }
      _try_iso_date: { Args: { p_value: string }; Returns: string }
      _try_json_numeric: { Args: { p_value: string }; Returns: number }
      _txn_matches_rule: {
        Args: {
          p_counterparty_id: string
          p_match_field: Database["public"]["Enums"]["rule_match_field"]
          p_match_kind: Database["public"]["Enums"]["text_match_kind"]
          p_match_value: string
          p_mcc: string
          p_purpose: string
          p_raw_name: string
        }
        Returns: boolean
      }
      _unresolved_receipt_chase_evidence: {
        Args: { p_transaction_id: string }
        Returns: {
          chase_log_id: string
          sent_at: string
          stage: number
          transaction_id: string
        }[]
      }
      allocate_eigenbeleg_number: {
        Args: { p_owner_entity: string; p_year: number }
        Returns: number
      }
      app_role: { Args: never; Returns: string }
      backfill_cardholder_from_cards: {
        Args: { p_dry_run?: boolean }
        Returns: Json
      }
      bulk_set_transaction_category: {
        Args: {
          p_category_id: string
          p_override_reason?: string
          p_transaction_ids: string[]
        }
        Returns: {
          account_id: string
          amount_eur: number | null
          amount_original: number
          bank_provided_state: string | null
          booked_at: string
          cardholder_name: string | null
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string | null
          counterparty_id: string | null
          counterparty_raw_name: string
          created_at: string
          currency: string
          fx_rate: number | null
          fx_source: string | null
          id: string
          import_file_id: string | null
          mcc: string | null
          purpose: string
          raw: Json
          source_id: string
          value_date: string
        }[]
        SetofOptions: {
          from: "*"
          to: "transactions"
          isOneToOne: false
          isSetofReturn: true
        }
      }
      cardholder_name_for_card: { Args: { p_card_id: string }; Returns: string }
      delete_vendor_portal_credentials: {
        Args: { p_vendor_slug: string }
        Returns: boolean
      }
      detect_first_refresh_token_rotation: { Args: never; Returns: string }
      extract_actor: { Args: never; Returns: undefined }
      get_receipt_digest: { Args: { p_digest_id: string }; Returns: Json }
      get_vendor_portal_credentials: {
        Args: { p_vendor_slug: string }
        Returns: {
          password: string
          username: string
          vendor_slug: string
        }[]
      }
      ingest_moneymoney_rows: {
        Args: {
          p_account_id: string
          p_decisions?: Json
          p_import_file_id?: string
          p_rows: Json
          p_window_fingerprint?: string
          p_window_from?: string
          p_window_to?: string
        }
        Returns: {
          account_id: string
          amount_original: number
          booked_at: string
          counterparty_id: string
          currency: string
          id: string
          purpose: string
          source_id: string
        }[]
      }
      ingest_revolut_txn: {
        Args: { p_account_id: string; p_payload: Json }
        Returns: string
      }
      ingest_revolut_txn_with_status: {
        Args: { p_account_id: string; p_payload: Json }
        Returns: Json
      }
      list_active_chase_threads: {
        Args: never
        Returns: {
          cardholder_name: string
          first_chased_at: string
          last_chased_at: string
          slack_thread_ref: string | null
          stage: number
          transaction_id: string
        }[]
      }
      list_first_time_counterparties: {
        Args: { p_limit?: number; p_period: string }
        Returns: {
          amount_eur_in_period: number
          canonical_name: string
          counterparty_id: string
          first_booked_at: string
          txns_in_period: number
          type: Database["public"]["Enums"]["counterparty_type"]
        }[]
      }
      list_vendor_portal_credentials: {
        Args: never
        Returns: {
          last_used_at: string | null
          updated_at: string
          username: string
          vendor_slug: string
        }[]
      }
      mark_receipt_digest_delivered: {
        Args: { p_digest_id: string; p_slack_ts: string }
        Returns: Json
      }
      mark_vendor_portal_used: {
        Args: { p_vendor_slug: string }
        Returns: string
      }
      moneymoney_purpose_norm_v3: {
        Args: { p_purpose: string }
        Returns: string
      }
      moneymoney_source_id_v2: { Args: { p_raw: Json }; Returns: string }
      moneymoney_source_id_v3: {
        Args: {
          p_amount_original: number
          p_booked_at: string
          p_raw: Json
          p_value_date: string
        }
        Returns: string
      }
      moneymoney_source_id_v4: {
        Args: {
          p_amount_original: number
          p_booked_at: string
          p_raw: Json
          p_value_date: string
        }
        Returns: string
      }
      persist_revolut_refresh_token: {
        Args: { p_new_token: string }
        Returns: undefined
      }
      re_resolve_for_rule: {
        Args: { p_rule_id: string }
        Returns: {
          new_categorization_status: Database["public"]["Enums"]["categorization_status"]
          new_category_id: string
          new_counterparty_id: string
          old_categorization_status: Database["public"]["Enums"]["categorization_status"]
          old_category_id: string
          old_counterparty_id: string
          transaction_id: string
        }[]
      }
      reconciliation_gaps: {
        Args: {
          p_account_id: string
          p_balance_end: number
          p_balance_start: number
          p_period: string
        }
        Returns: {
          account_currency: string
          account_id: string
          account_name: string
          balance_change: number
          balance_end: number
          balance_start: number
          flagged: boolean
          gap: number
          period: string
          period_end: string
          period_start: string
          txn_sum: number
        }[]
      }
      reconciliation_grid: {
        Args: {
          p_account_ids?: string[]
          p_from: string
          p_from_date?: string
          p_to: string
          p_to_date?: string
        }
        Returns: {
          account_currency: string
          account_id: string
          account_name: string
          period: string
          status: string
          txn_sum: number
        }[]
      }
      record_export_batch: {
        Args: { batch: Json; members: Json }
        Returns: Json
      }
      record_import_file_attempt: {
        Args: {
          p_account_id: string
          p_actor: string
          p_byte_size: number
          p_file_hash: string
          p_original_filename: string
        }
        Returns: {
          account_id: string
          attempt_count: number
          byte_size: number
          commit_summary: Json | null
          committed_at: string | null
          created_at: string
          created_by: string
          file_hash: string
          id: string
          last_actor: string
          last_attempt_at: string
          last_error: Json | null
          original_filename: string
          row_count: number | null
          source: string
          status: Database["public"]["Enums"]["import_file_status"]
          storage_path: string
        }
        SetofOptions: {
          from: "*"
          to: "import_files"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      record_import_file_outcome: {
        Args: {
          p_commit_summary?: Json
          p_error?: Json
          p_import_file_id: string
          p_outcome: string
          p_row_count?: number
        }
        Returns: {
          account_id: string
          attempt_count: number
          byte_size: number
          commit_summary: Json | null
          committed_at: string | null
          created_at: string
          created_by: string
          file_hash: string
          id: string
          last_actor: string
          last_attempt_at: string
          last_error: Json | null
          original_filename: string
          row_count: number | null
          source: string
          status: Database["public"]["Enums"]["import_file_status"]
          storage_path: string
        }
        SetofOptions: {
          from: "*"
          to: "import_files"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      resolve_needs_human_review: {
        Args: { p_resolution_note: string; p_transaction_id: string }
        Returns: Json
      }
      resolve_pending_team_member_upload: {
        Args: { p_resolution_note: string; p_transaction_id: string }
        Returns: Json
      }
      resolve_receipt_upload: {
        Args: { p_actor?: string; p_document_id: string; p_log_id: string }
        Returns: Json
      }
      revolut_secret: { Args: { p_name: string }; Returns: string }
      revolut_sync_state_janitor: { Args: never; Returns: number }
      rule_affected_count: { Args: { p_rule_id: string }; Returns: number }
      rule_affected_sample: {
        Args: { p_limit: number; p_rule_id: string }
        Returns: {
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string
          counterparty_id: string
          counterparty_raw_name: string
          id: string
        }[]
      }
      search_transactions_mcp: {
        Args: {
          p_account_id?: string
          p_after_booked_at?: string
          p_after_id?: string
          p_amount_max_eur?: number
          p_amount_min_eur?: number
          p_categorization_status?: Database["public"]["Enums"]["categorization_status"]
          p_category_id?: string
          p_counterparty_id?: string
          p_counterparty_raw_name_contains?: string
          p_from_date?: string
          p_linked_status?: string
          p_probe_limit?: number
          p_purpose_contains?: string
          p_to_date?: string
        }
        Returns: {
          account_id: string
          amount_eur: number
          amount_original: number
          booked_at: string
          cardholder_name: string
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string
          counterparty_id: string
          counterparty_raw_name: string
          currency: string
          id: string
          mcc: string
          purpose: string
          value_date: string
        }[]
      }
      send_receipt_digest: {
        Args: {
          p_booked_since?: string
          p_dry_run?: boolean
          p_max_items?: number
          p_team_member_id: string
          p_today: string
        }
        Returns: Json
      }
      send_receipt_request: {
        Args: {
          p_booked_since?: string
          p_stage: number
          p_token_ttl_hours?: number
          p_transaction_id: string
          p_upload_url_base?: string
        }
        Returns: Json
      }
      set_transaction_category: {
        Args: {
          p_category_id: string
          p_override_reason?: string
          p_transaction_id: string
        }
        Returns: {
          account_id: string
          amount_eur: number | null
          amount_original: number
          bank_provided_state: string | null
          booked_at: string
          cardholder_name: string | null
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string | null
          counterparty_id: string | null
          counterparty_raw_name: string
          created_at: string
          currency: string
          fx_rate: number | null
          fx_source: string | null
          id: string
          import_file_id: string | null
          mcc: string | null
          purpose: string
          raw: Json
          source_id: string
          value_date: string
        }
        SetofOptions: {
          from: "*"
          to: "transactions"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      transactions_awaiting_receipt: {
        Args: { p_booked_since?: string; p_today: string }
        Returns: {
          account_id: string
          amount_original: number
          booked_at: string
          cardholder_name: string
          counterparty_raw_name: string
          currency: string
          display_name: string
          last_sent_at: string
          last_stage_sent: number
          next_due_at: string
          next_due_stage: number
          slack_user_id: string
          team_member_id: string
          transaction_id: string
        }[]
      }
      unassign_document: {
        Args: { p_assignment_id: string; p_reason: string }
        Returns: Json
      }
      unmatched_with_buckets: {
        Args: { p_from_date?: string; p_to_date?: string }
        Returns: {
          bucket: Database["public"]["Enums"]["bucket_code"]
          evidence: Json
          suggested_action_code: string
          transaction_id: string
        }[]
      }
      unset_transaction_category: {
        Args: { p_transaction_id: string }
        Returns: {
          account_id: string
          amount_eur: number | null
          amount_original: number
          bank_provided_state: string | null
          booked_at: string
          cardholder_name: string | null
          categorization_status: Database["public"]["Enums"]["categorization_status"]
          category_id: string | null
          counterparty_id: string | null
          counterparty_raw_name: string
          created_at: string
          currency: string
          fx_rate: number | null
          fx_source: string | null
          id: string
          import_file_id: string | null
          mcc: string | null
          purpose: string
          raw: Json
          source_id: string
          value_date: string
        }
        SetofOptions: {
          from: "*"
          to: "transactions"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      upsert_vendor_portal_credentials: {
        Args: { p_password: string; p_username: string; p_vendor_slug: string }
        Returns: string
      }
    }
    Enums: {
      account_source_of_record: "api:revolut" | "csv:moneymoney"
      audit_action:
        | "create"
        | "update"
        | "delete"
        | "re_resolve"
        | "source_id_rehash"
      bucket_code:
        | "no_invoice_expected_marker_present"
        | "needs_human_review_marker_present"
        | "awaiting_cardholder"
        | "cooldown_active"
        | "source_unavailable"
        | "counterparty_unknown"
        | "no_candidate_found"
        | "amount_mismatch"
        | "no_attempt_yet"
        | "manual_review"
      categorization_status:
        | "uncategorized"
        | "auto"
        | "agent_proposed"
        | "manual"
      confidence_level: "low" | "medium" | "high"
      counterparty_type: "vendor" | "employee" | "client" | "authority" | "self"
      dedup_decision: "duplicate" | "distinct"
      document_assignment_source:
        | "revolut_api"
        | "manual"
        | "agent_match"
        | "cardholder_link"
      document_source:
        | "manual_upload"
        | "revolut_api"
        | "email_inbox"
        | "eigenbeleg"
      extraction_status:
        | "pending"
        | "success"
        | "failed"
        | "na"
        | "not_required"
      import_file_status: "previewed" | "committed" | "rejected" | "failed"
      match_attempt_outcome:
        | "match_attached"
        | "no_match"
        | "cooldown_skipped"
        | "source_unavailable"
      match_attempt_source: "inbox" | "gmail" | "disk" | "portal"
      revolut_sync_status:
        | "success"
        | "failed_refresh_token_invalid"
        | "failed_token_persistence"
        | "failed_api_fetch"
        | "failed_db_write"
        | "failed_unexpected"
      rule_match_field:
        | "counterparty_raw_name"
        | "purpose"
        | "mcc"
        | "counterparty_id"
      rule_source:
        | "migrated_v1"
        | "manual"
        | "agent_proposed"
        | "agent_committed"
      rule_status: "proposed" | "active" | "inactive"
      rule_type:
        | "alias"
        | "category"
        | "internal_transfer"
        | "ignore"
        | "no_invoice_expected"
      text_match_kind: "exact" | "substring" | "regex"
      transaction_link_detected_by: "system_rule" | "manual" | "revolut_api"
      transaction_link_type:
        | "internal_transfer"
        | "refund"
        | "manual_exclude"
        | "no_invoice_expected"
        | "pending_team_member_upload"
        | "bank_reversal"
        | "needs_human_review"
        | "ar_settlement"
        | "own_account_transfer"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  graphql_public: {
    Enums: {},
  },
  public: {
    Enums: {
      account_source_of_record: ["api:revolut", "csv:moneymoney"],
      audit_action: [
        "create",
        "update",
        "delete",
        "re_resolve",
        "source_id_rehash",
      ],
      bucket_code: [
        "no_invoice_expected_marker_present",
        "needs_human_review_marker_present",
        "awaiting_cardholder",
        "cooldown_active",
        "source_unavailable",
        "counterparty_unknown",
        "no_candidate_found",
        "amount_mismatch",
        "no_attempt_yet",
        "manual_review",
      ],
      categorization_status: [
        "uncategorized",
        "auto",
        "agent_proposed",
        "manual",
      ],
      confidence_level: ["low", "medium", "high"],
      counterparty_type: ["vendor", "employee", "client", "authority", "self"],
      dedup_decision: ["duplicate", "distinct"],
      document_assignment_source: [
        "revolut_api",
        "manual",
        "agent_match",
        "cardholder_link",
      ],
      document_source: [
        "manual_upload",
        "revolut_api",
        "email_inbox",
        "eigenbeleg",
      ],
      extraction_status: ["pending", "success", "failed", "na", "not_required"],
      import_file_status: ["previewed", "committed", "rejected", "failed"],
      match_attempt_outcome: [
        "match_attached",
        "no_match",
        "cooldown_skipped",
        "source_unavailable",
      ],
      match_attempt_source: ["inbox", "gmail", "disk", "portal"],
      revolut_sync_status: [
        "success",
        "failed_refresh_token_invalid",
        "failed_token_persistence",
        "failed_api_fetch",
        "failed_db_write",
        "failed_unexpected",
      ],
      rule_match_field: [
        "counterparty_raw_name",
        "purpose",
        "mcc",
        "counterparty_id",
      ],
      rule_source: [
        "migrated_v1",
        "manual",
        "agent_proposed",
        "agent_committed",
      ],
      rule_status: ["proposed", "active", "inactive"],
      rule_type: [
        "alias",
        "category",
        "internal_transfer",
        "ignore",
        "no_invoice_expected",
      ],
      text_match_kind: ["exact", "substring", "regex"],
      transaction_link_detected_by: ["system_rule", "manual", "revolut_api"],
      transaction_link_type: [
        "internal_transfer",
        "refund",
        "manual_exclude",
        "no_invoice_expected",
        "pending_team_member_upload",
        "bank_reversal",
        "needs_human_review",
        "ar_settlement",
        "own_account_transfer",
      ],
    },
  },
} as const

